/**
 * 稽核日誌 (/audit, boards/Audit.dc.html) — admin+. Server-filtered, server-paged audit log
 * (GET /api/audit) with search / source / category / period, and a CSV export of the current
 * filter (GET /api/audit/export). `?q=` lets the lookup drawer land here pre-searched.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pager } from '@/components/Pager';
import { ToneTag } from '@/components/RosterBits';
import { errMsg } from '@/components/Dialogs';
import { usePoll } from '@/lib/usePoll';
import { api } from '@/lib/api';
import { daysAgo } from '@/lib/dates';
import { fmtDate } from '@/lib/format';
import { useDebounced } from '@/lib/useDebounced';
import { SEARCH_TOO_LONG, searchTooLong } from '@/lib/searchLimit';
import { AUDIT_CATEGORY_LABEL, SOURCE_LABEL, SOURCE_TONE, auditActionLabel } from '@/lib/labels';
import { auditActor, auditSentence, isRoutineAudit } from '@/lib/auditText';
import type { AuditActorType, AuditCategory, AuditEntry, AuditQuery, Paged } from '@/types';

const PERIODS = [
  { id: '7', label: '近 7 日', days: 7 },
  { id: '30', label: '近 30 日', days: 30 },
  { id: '90', label: '近 90 日', days: 90 },
  { id: 'all', label: '全部', days: 0 },
] as const;
type PeriodId = (typeof PERIODS)[number]['id'];

const SOURCES: AuditActorType[] = ['admin', 'app', 'system'];
const CATEGORIES = Object.keys(AUDIT_CATEGORY_LABEL) as AuditCategory[];
const LIMIT = 100;

export function Audit() {
  const [sp, setSp] = useSearchParams();
  const q = sp.get('q') ?? '';
  const source = (SOURCES as string[]).includes(sp.get('source') ?? '') ? (sp.get('source') as AuditActorType) : undefined;
  const category = (CATEGORIES as string[]).includes(sp.get('category') ?? '') ? (sp.get('category') as AuditCategory) : undefined;
  // A deep link with ?q= (from a machine) wants the whole history, not just the last week.
  const periodRaw = sp.get('period') ?? (q ? 'all' : '7');
  const period: PeriodId = (PERIODS.some((p) => p.id === periodRaw) ? periodRaw : '7') as PeriodId;
  const page = Math.max(1, Number(sp.get('page')) || 1);
  // Routine device sign-in rows are hidden on the page only (the Worker has no exclude filter);
  // paging and totals stay server-honest and the hidden count is shown under the table.
  const showRoutine = sp.get('routine') === '1';

  const [qInput, setQInput] = useState(q);
  const qDebounced = useDebounced(qInput.trim(), 350);
  const qTooLong = searchTooLong(qInput);
  useEffect(() => {
    if (searchTooLong(qDebounced)) return; // the Worker would 400; keep the last good search
    if (qDebounced !== q) set({ q: qDebounced || null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced]);

  function set(patch: Record<string, string | null>, keepPage = false) {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    if (!keepPage) next.delete('page');
    setSp(next, { replace: true });
  }

  const p = PERIODS.find((x) => x.id === period) ?? PERIODS[0];
  const filter: Omit<AuditQuery, 'page' | 'limit'> = {
    q: q || undefined,
    source,
    category,
    from: p.days ? daysAgo(p.days - 1) : undefined,
  };

  const list = usePoll<Paged<AuditEntry>>(() => api.listAuditPaged({ ...filter, page, limit: LIMIT }), 30_000, [sp.toString()]);
  const all = list.data?.entries ?? [];
  const rows = showRoutine ? all : all.filter((r) => !isRoutineAudit(r));
  const hidden = all.length - rows.length;

  return (
    <div className="v2-page">
      <div className="v2-head">
        <div className="titles">
          <h1 className="v2-title">稽核日誌</h1>
          <p className="v2-sub">所有後台操作、App 端的授權事件與系統排程都在這裡;紀錄與變更在同一筆交易寫入</p>
        </div>
        <a className="btn btn-outline" href={api.auditExportUrl(filter)} download>匯出目前結果(CSV)</a>
      </div>

      <div className="filterbar">
        <label className="v2-field" style={{ flex: 1, minWidth: 240 }}>搜尋
          <input className="v2-input" value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="操作、操作者 Email、對象(序號、機型、工單)" aria-invalid={qTooLong || undefined} />
          {qTooLong && <span className="error-line" role="alert">{SEARCH_TOO_LONG}</span>}
        </label>
        <label className="v2-field">來源
          <select className="v2-select" value={source ?? ''} onChange={(e) => set({ source: e.target.value || null })}>
            <option value="">全部(後台 / App / 系統)</option>
            {SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
          </select>
        </label>
        <label className="v2-field">操作類型
          <select className="v2-select" value={category ?? ''} onChange={(e) => set({ category: e.target.value || null })}>
            <option value="">全部</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{AUDIT_CATEGORY_LABEL[c]}</option>)}
          </select>
        </label>
        <label className="v2-field">期間
          <select className="v2-select" value={period} onChange={(e) => set({ period: e.target.value === '7' && !q ? null : e.target.value })}>
            {PERIODS.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
          </select>
        </label>
        <label className="opt-row" style={{ minHeight: 34 }} title="App 每次連線或更新連線憑證都會留一筆紀錄">
          <input type="checkbox" checked={showRoutine} onChange={(e) => set({ routine: e.target.checked ? '1' : null }, true)} />
          顯示例行紀錄
        </label>
      </div>

      <section className="panel flush tpanel">
        {Boolean(list.error) && !list.data && <div className="error-line" style={{ padding: 'var(--space-3) var(--space-4)' }}>載入失敗:{errMsg(list.error)}</div>}
        <div className="tscroll">
          <table className="v2-table">
            <thead>
              <tr>
                <th style={{ width: 150 }}>時間</th>
                <th style={{ width: 80 }}>來源</th>
                <th style={{ width: 180 }}>操作</th>
                <th>對象與內容</th>
                <th style={{ width: 200 }}>操作者</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="empty-row"><td colSpan={5}>{list.loading && !list.data ? '載入中…' : hidden > 0 ? '這一頁只有例行紀錄;勾選「顯示例行紀錄」查看,或翻到下一頁' : '這個期間沒有符合的紀錄'}</td></tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.id}>
                    <td className="muted num nowrap">{fmtDate(r.ts)}</td>
                    <td><ToneTag tone={SOURCE_TONE[r.actor_type] ?? 'neutral'}>{SOURCE_LABEL[r.actor_type] ?? r.actor_type}</ToneTag></td>
                    <td>{auditActionLabel(r.action)}</td>
                    <td className="ellipsis" style={{ maxWidth: 520 }} title={auditSentence(r)}>{auditSentence(r)}</td>
                    <td className="muted ellipsis" style={{ maxWidth: 200 }} title={auditActor(r).title}>{auditActor(r).text}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {hidden > 0 && (
          <div className="muted-note" style={{ padding: 'var(--space-2) var(--space-4) 0' }}>
            這一頁另有 {hidden} 筆例行紀錄(App 裝置連線)已隱藏;下方的筆數與頁數仍包含它們
          </div>
        )}
        <Pager
          prefix={p.label}
          page={page}
          limit={LIMIT}
          total={list.data?.total ?? 0}
          unit="筆"
          onPage={(n) => set({ page: n === 1 ? null : String(n) }, true)}
        />
      </section>
    </div>
  );
}

